/**
 * Unified date formatting utility.
 *
 * Handles all the edge cases found across the codebase:
 * - null/undefined/empty string → fallback
 * - Object with date-like properties → extracts and formats
 * - ISO strings, datetime strings with spaces, microseconds
 * - Invalid dates → fallback
 *
 * @param {string|Date|object} input - The date to format
 * @param {object} [options]
 * @param {'short'|'medium'|'long'} [options.format='short'] - Display format
 *   - 'short':  "3/26/2026"
 *   - 'medium': "Mar 26, 2026"
 *   - 'long':   "Thu, Mar 26, 2026"
 * @param {string} [options.fallback='—'] - Returned when input is invalid
 * @returns {string}
 */

const FORMAT_OPTIONS = {
  short: { month: 'numeric', day: 'numeric', year: 'numeric' },
  medium: { month: 'short', day: 'numeric', year: 'numeric' },
  long: { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' },
  datetime: { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' },
};

// Common property names that hold a date value on API response objects
const DATE_PROPS = ['createdat', 'created_at', 'createdAt', 'date', 'timestamp', 'updatedAt', 'updated_at'];

export default function formatDate(input, { format = 'short', fallback = '—' } = {}) {
  // Null / undefined / empty
  if (input == null || input === '') return fallback;

  // If input is an object (not a Date), try to extract a date property
  if (typeof input === 'object' && !(input instanceof Date)) {
    if (Object.keys(input).length === 0) return fallback;
    for (const prop of DATE_PROPS) {
      if (input[prop]) return formatDate(input[prop], { format, fallback });
    }
    return fallback;
  }

  // Parse
  let date;
  if (input instanceof Date) {
    date = input;
  } else if (typeof input === 'string') {
    let cleaned = input;
    // Strip microseconds (e.g. "2026-03-26 12:00:00.123456")
    if (cleaned.includes('.') && !cleaned.includes('+') && !cleaned.includes('Z')) {
      cleaned = cleaned.split('.')[0];
    }
    date = new Date(cleaned);
    // Retry with T separator if space-separated datetime failed
    if (isNaN(date.getTime()) && cleaned.includes(' ')) {
      date = new Date(cleaned.replace(' ', 'T'));
    }
  } else {
    return fallback;
  }

  if (isNaN(date.getTime())) return fallback;

  // Relative format: "Just now", "5m ago", "3h ago", "2d ago", then falls back to medium
  if (format === 'relative') {
    const ms = Date.now() - date.getTime();
    if (ms < 0) return fallback;
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    // Fall back to medium format for older dates
    return date.toLocaleDateString('en-US', FORMAT_OPTIONS.medium);
  }

  try {
    return date.toLocaleDateString('en-US', FORMAT_OPTIONS[format] || FORMAT_OPTIONS.short);
  } catch {
    return fallback;
  }
}
